/**
 * Global UTC <-> local-time conversion helpers used by the HTTP interceptor.
 *
 * Contract for this codebase:
 *  - Outgoing requests: every JS `Date` instance found in the body is replaced
 *    by its UTC ISO 8601 representation (`toISOString()`), so the API always
 *    receives UTC strings with a trailing `Z`.
 *  - Incoming responses: every string that matches an ISO 8601 datetime is
 *    NORMALIZED so it ends with a `Z`. We do NOT convert strings into JS Date
 *    objects, because dozens of components in this codebase do string ops
 *    (`.split('T')`, `.substring(0, 10)`, etc.) on these fields, and turning
 *    them into Dates would break those callers. The string stays a string,
 *    but now every downstream `new Date(stringField)` correctly parses it as
 *    UTC and renders in the browser's local timezone.
 *
 * Why we treat un-suffixed strings as UTC:
 *    The .NET backend often serializes `DateTime` values with
 *    `DateTimeKind.Unspecified`, producing strings like `"2026-04-10T11:53:51"`
 *    with no timezone marker. By contract, these are UTC in this system. If we
 *    let `new Date("...51")` parse them, the browser would treat them as local
 *    time and shift the value by the user's offset, which is wrong. The helper
 *    appends `Z` so the string is unambiguously UTC before any consumer reads
 *    it.
 */

/**
 * Matches strict ISO 8601 datetime strings: a `YYYY-MM-DD` date, a `T`
 * separator, an `HH:MM:SS` time, an optional fractional second, and an
 * OPTIONAL timezone marker (`Z`, `+HH:MM`, `+HHMM`, `-HH:MM`, `-HHMM`).
 *
 * Date-only strings (`"2026-04-10"`) are intentionally NOT matched: they have
 * no time component and converting them to a `Date` would just introduce
 * timezone ambiguity.
 */
const ISO_DATETIME_RE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/;

/** True when the string already carries an explicit timezone marker. */
const HAS_EXPLICIT_TZ_RE = /(?:Z|[+-]\d{2}:?\d{2})$/;

/**
 * Normalize an ISO datetime string so it unambiguously represents UTC: if it
 * already has a `Z` or `±HH:MM` offset, return it unchanged; otherwise append
 * `Z`. The result is still a string — callers that need a `Date` should call
 * `new Date(...)` themselves.
 */
export function normalizeApiDateString(value: string): string {
  return HAS_EXPLICIT_TZ_RE.test(value) ? value : value + 'Z';
}

/**
 * Parse an ISO datetime string as UTC, regardless of whether the input has a
 * trailing `Z`. Convenience wrapper around `normalizeApiDateString` + `Date`.
 */
export function parseApiDate(value: string): Date {
  return new Date(normalizeApiDateString(value));
}

/**
 * Returns true when the value is a JSON-walkable container (plain object or
 * array). Anything else (FormData, Blob, ArrayBuffer, primitives, null, Date)
 * is left untouched by the walker.
 */
function isWalkable(
  value: unknown
): value is Record<string, unknown> | unknown[] {
  if (value === null || typeof value !== 'object') return false;
  if (Array.isArray(value)) return true;
  if (
    (typeof FormData !== 'undefined' && value instanceof FormData) ||
    (typeof Blob !== 'undefined' && value instanceof Blob) ||
    (typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer) ||
    value instanceof Date
  ) {
    return false;
  }
  // Treat anything else with Object prototype (or null prototype) as walkable.
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Recursively walks `body` and replaces every `Date` instance with its UTC
 * ISO 8601 string. Mutates the body in place and returns it.
 *
 * Safe to call on `null`, primitives, FormData, Blob, etc. — those are
 * returned unchanged.
 */
export function convertDatesToUtcInPlace<T>(
  body: T,
  seen: WeakSet<object> = new WeakSet()
): T {
  if (body === null || body === undefined) return body;

  // Top-level Date — caller usually passes an object, but support this too.
  if (body instanceof Date) {
    return body.toISOString() as unknown as T;
  }

  if (!isWalkable(body)) return body;
  if (seen.has(body as object)) return body;
  seen.add(body as object);

  if (Array.isArray(body)) {
    for (let i = 0; i < body.length; i++) {
      const v = body[i];
      if (v instanceof Date) {
        body[i] = v.toISOString();
      } else if (isWalkable(v)) {
        convertDatesToUtcInPlace(v, seen);
      }
    }
    return body;
  }

  const obj = body as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    const v = obj[key];
    if (v instanceof Date) {
      obj[key] = v.toISOString();
    } else if (isWalkable(v)) {
      convertDatesToUtcInPlace(v, seen);
    }
  }
  return body;
}

/**
 * Recursively walks `body` and, for every string matching an ISO 8601
 * datetime that is missing its timezone marker, appends `Z` so the value is
 * unambiguously UTC. Strings that already have `Z` or an explicit `±HH:MM`
 * offset are left untouched. Strings remain strings — no `Date` instances
 * are created. Mutates the body in place and returns it.
 *
 * This is the safe, drop-in form of "make API responses UTC-correct":
 * existing component code that does `.split('T')`, `.substring(0, 10)`, or
 * `new Date(value)` keeps working, but `new Date(value)` now parses as UTC.
 *
 * Safe to call on `null`, primitives, FormData, Blob, etc. — those are
 * returned unchanged.
 */
export function convertDatesFromUtcInPlace<T>(
  body: T,
  seen: WeakSet<object> = new WeakSet()
): T {
  if (body === null || body === undefined) return body;

  // Top-level string (rare for HTTP responses, but support it).
  if (typeof body === 'string') {
    return (ISO_DATETIME_RE.test(body)
      ? normalizeApiDateString(body)
      : body) as unknown as T;
  }

  if (!isWalkable(body)) return body;
  if (seen.has(body as object)) return body;
  seen.add(body as object);

  if (Array.isArray(body)) {
    for (let i = 0; i < body.length; i++) {
      const v = body[i];
      if (typeof v === 'string') {
        if (ISO_DATETIME_RE.test(v)) body[i] = normalizeApiDateString(v);
      } else if (isWalkable(v)) {
        convertDatesFromUtcInPlace(v, seen);
      }
    }
    return body;
  }

  const obj = body as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    const v = obj[key];
    if (typeof v === 'string') {
      if (ISO_DATETIME_RE.test(v)) obj[key] = normalizeApiDateString(v);
    } else if (isWalkable(v)) {
      convertDatesFromUtcInPlace(v, seen);
    }
  }
  return body;
}
